import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Card,
  Form,
  Offcanvas,
  FloatingLabel,
  Spinner,
} from "olive-react-myoneui";
import DropdownCommon from "../component/DropdownCommon";
import {
  getTopics,
  getSubTopics,
  updateQuestion,
  fetchAllQuestions,
} from "../../../api/questionApis";

const placeholderImg = "https://placehold.co/600x400?text=No+image";

function QuestionEditOffcanvas({ show, onClose, initialData = {}, onSuccess }) {
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);
  const [existingQuestions, setExistingQuestions] = useState([]);

  // Form fields
  const [paper, setPaper] = useState("");
  const [year, setYear] = useState("");
  const [questionNumber, setQuestionNumber] = useState("");
  const [part, setPart] = useState("");
  const [subpart, setSubpart] = useState("");
  const [marks, setMarks] = useState("");
  const [order, setOrder] = useState(1);

  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubTopic, setSelectedSubTopic] = useState(null);
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [questionFile, setQuestionFile] = useState(null);
  const [msFile, setMsFile] = useState(null);

  const [showDependencies, setShowDependencies] = useState(false);
  const [dependencies, setDependencies] = useState([]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // State for dependency pagination
  const [dependencyPage, setDependencyPage] = useState(1);
  const [hasMoreDependencies, setHasMoreDependencies] = useState(true);
  const [loadingDependencies, setLoadingDependencies] = useState(false);

  const sectionList = [
    { name: "Section A" },
    { name: "Section B" },
    { name: "Section C" },
    { name: "Section D" },
  ];

  const typeList = [{ name: "Question" }, { name: "Context" }];
  const allowedImageTypes = ["image/jpeg", "image/png", "image/jpg"];
  const [topicSearch, setTopicSearch] = useState("");
  const [subTopicSearch, setSubTopicSearch] = useState("");

  const fetchDependencies = useCallback(async (page) => {
    setLoadingDependencies(true);
    try {
      const questionResp = await fetchAllQuestions(page, 10);
      if (questionResp.data.success) {
        const newQuestions = questionResp.data.data || [];
        setExistingQuestions((prev) => (page === 1 ? newQuestions : [...prev, ...newQuestions]));
        setHasMoreDependencies(newQuestions.length === 10);
      }
    } catch (err) {
      setErrors((prev) => ({ ...prev, fetchData: "Error fetching dependencies" }));
    } finally {
      setLoadingDependencies(false);
    }
  }, []);

  useEffect(() => {
    if (!show) return;

    const fetchInitialData = async () => {
      try {
        const topicResp = await getTopics();
        if (topicResp.data.success) setTopics(topicResp.data.data || []);
      } catch (err) {
        console.error(err);
      }
    };

    fetchInitialData();
    fetchDependencies(1);

    if (initialData._id) {
      populateForm(initialData);
    } else {
      resetForm();
    }
  }, [show, initialData, fetchDependencies]);

  const populateForm = (data) => {
    setPaper(data.paper || "");
    setYear(data.year || "");
    setQuestionNumber(data.question_number || "");
    setPart(data.part || "");
    setSubpart(data.subpart || "");
    setMarks(data.marks || "");
    setOrder(data.order || 1);
    setSelectedSection(data.section || "");
    setSelectedType(data.type || "Question");

    // Reset dependencies based on backend data
    const initialDependencies = data.dependencies || [];
    if (initialDependencies.length > 0) {
      const formattedDependencies = initialDependencies.map((dep) => {
        if (typeof dep === "string") return { questionId: dep, is_context: true };
        if (dep._id) return { questionId: dep._id, is_context: true };
        return dep;
      });
      setDependencies(formattedDependencies);
      setShowDependencies(true);
    } else {
      setDependencies([]);
      setShowDependencies(false);
    }

    // Set topic/subtopic
    if (data.topics?.[0]) {
      const initialTopic = { _id: data.topics[0]._id, name: data.topics[0].title };
      setSelectedTopic(initialTopic);
      handleTopicChange(initialTopic, data.subtopics?.[0]);
    }

    setErrors({});
    setLoading(false);
  };

  const resetForm = () => {
    setPaper("");
    setYear("");
    setQuestionNumber("");
    setPart("");
    setSubpart("");
    setMarks("");
    setOrder(1);
    setSelectedTopic(null);
    setSelectedSubTopic(null);
    setSelectedSection("");
    setSelectedType("");
    setQuestionFile(null);
    setMsFile(null);
    setShowDependencies(false);
    setDependencies([]);
    setErrors({});
    setLoading(false);
    setTopicSearch("");
    setSubTopicSearch("");
    setSubTopics([]);
    setDependencyPage(1);
    setHasMoreDependencies(true);
    setExistingQuestions([]);
  };

  const trimLeadingSpaces = (text) => text.replace(/^\s+/, "");

  const handleDigitOnlyInput = (value, setter) => {
    const digitsOnly = value.replace(/\D/g, "");
    setter(digitsOnly);
  };

  const handleTopicChange = (topic, initialSubTopic = null) => {
    setSelectedTopic(topic);
    setSelectedSubTopic(null);
    setSubTopics([]);
    setSubTopicSearch("");

    if (!topic?._id) return;

    getSubTopics(topic._id)
      .then((res) => {
        if (res.data.success) {
          const subtopicList = (res.data.data || []).map((s) => ({
            _id: s._id,
            name: s.title,
          }));
          setSubTopics(subtopicList);
          if (initialSubTopic) {
            const subTopicToSelect = subtopicList.find(
              (st) => st._id === initialSubTopic._id
            );
            if (subTopicToSelect) setSelectedSubTopic(subTopicToSelect);
          }
        }
      })
      .catch((err) => console.error(err));
  };

  const handleImageFileValidation = (file, setterFunction) => {
    if (!file) return;
    if (!allowedImageTypes.includes(file.type)) {
      setterFunction(null);
      return;
    }
    setterFunction(file);
  };

  const handleDependencyToggle = (questionId, is_context) => {
    const existing = dependencies.find((d) => d.questionId === questionId);
    if (existing) {
      setDependencies(dependencies.filter((d) => d.questionId !== questionId));
    } else {
      setDependencies([...dependencies, { questionId, is_context }]);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!paper) newErrors.paper = "Paper is required";
    if (!year) newErrors.year = "Year is required";
    if (!questionNumber) newErrors.questionNumber = "Question number is required";
    if (!selectedTopic) newErrors.topic = "Topic is required";
    if (!selectedSubTopic) newErrors.subTopic = "Subtopic is required";
    if (!selectedSection) newErrors.section = "Section is required";
    if (!selectedType) newErrors.type = "Type is required";

    if (showDependencies && dependencies.length === 0) {
      newErrors.dependencies = "Please select at least one dependency.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setErrors({});
      const formData = new FormData();
      formData.append("paper", paper);
      formData.append("year", year);
      formData.append("question_number", questionNumber);
      formData.append("part", part);
      formData.append("subpart", subpart);
      formData.append("section", selectedSection);
      formData.append("marks", selectedType === "Question" ? Number(marks) : 0);
      formData.append("order", order);
      formData.append("topics", JSON.stringify([selectedTopic._id]));
      formData.append("subtopics", JSON.stringify([selectedSubTopic._id]));

      if (questionFile) formData.append("question_file", questionFile);
      if (msFile) formData.append("ms_file", msFile);

      // Always send dependencies
      formData.append(
        "dependencies",
        JSON.stringify(showDependencies ? dependencies : [])
      );

      const response = await updateQuestion(initialData._id, formData);

      if (response.data.success) {
        onSuccess?.();
        onClose();
      } else if (response.data.errors) {
        const fieldErrors = {};
        response.data.errors.forEach((err) => {
          fieldErrors[err.param] = err.msg;
        });
        setErrors(fieldErrors);
      }
    } catch (err) {
      console.error(err);
      if (err.response?.data?.errors) {
        const fieldErrors = {};
        err.response.data.errors.forEach((error) => {
          fieldErrors[error.param] = error.msg;
        });
        setErrors(fieldErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredTopics = topics.filter((t) =>
    t.title.toLowerCase().includes(topicSearch.toLowerCase())
  );
  const filteredSubTopics = subTopics.filter((s) =>
    s.name.toLowerCase().includes(subTopicSearch.toLowerCase())
  );

  const handleDependencyScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 5 &&
      hasMoreDependencies &&
      !loadingDependencies
    ) {
      const nextPage = dependencyPage + 1;
      setDependencyPage(nextPage);
      fetchDependencies(nextPage);
    }
  };

  return (
    <Offcanvas show={show} onHide={onClose} placement="end">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Edit Question</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        {/* PAPER */}
        <FloatingLabel
          controlId="paperId"
          label={<span className={errors.paper && "text-danger"}>Paper *</span>}
          className="mb-3"
        >
          <Form.Control
            type="text"
            placeholder="Enter Paper"
            value={paper}
            onChange={(e) => setPaper(trimLeadingSpaces(e.target.value))}
          />
          {errors.paper && <div className="text-danger mt-1">{errors.paper}</div>}
        </FloatingLabel>

        {/* YEAR */}
        <Form.Group controlId="year" className="mb-3">
          <Form.Label>Year *</Form.Label>
          <Form.Select value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="">Select Year</option>
            {Array.from(
              { length: new Date().getFullYear() - 2000 + 1 },
              (_, i) => 2000 + i
            )
              .reverse()
              .map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
          </Form.Select>
          {errors.year && <div className="text-danger mt-1">{errors.year}</div>}
        </Form.Group>

        {/* QUESTION FILE */}
        <Form.Group className="mb-3">
          <Form.Label className={errors.questionFile && "text-danger"}>
            Question File *
          </Form.Label>
          <Form.Control
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={(e) =>
              handleImageFileValidation(e.target.files[0], setQuestionFile)
            }
          />
        </Form.Group>

        {initialData?.question_file && (
          <img
            src={initialData.question_file}
            alt="Question"
            style={{ width: "100%", marginBottom: "1rem" }}
            onError={(e) => (e.target.src = placeholderImg)}
          />
        )}

        {/* MS FILE */}
        <Form.Group className="mb-3">
          <Form.Label>Marking Scheme File</Form.Label>
          <Form.Control
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={(e) => handleImageFileValidation(e.target.files[0], setMsFile)}
          />
        </Form.Group>

        {initialData?.ms_file && (
          <img
            src={initialData.ms_file}
            alt="Marking Scheme"
            style={{ width: "100%", marginBottom: "1rem" }}
            onError={(e) => (e.target.src = placeholderImg)}
          />
        )}

        {/* QUESTION NUMBER */}
        <FloatingLabel
          controlId="questionNumberId"
          label={<span className={errors.questionNumber && "text-danger"}>Question Number *</span>}
          className="mb-3"
        >
          <Form.Control
            type="text"
            placeholder="Enter Question Number"
            value={questionNumber}
            onChange={(e) => handleDigitOnlyInput(e.target.value, setQuestionNumber)}
          />
          {errors.questionNumber && (
            <div className="text-danger mt-1">{errors.questionNumber}</div>
          )}
        </FloatingLabel>

        {/* PART */}
        <FloatingLabel controlId="partId" label="Part" className="mb-3">
          <Form.Control
            type="text"
            placeholder="Enter Part"
            value={part}
            onChange={(e) => setPart(trimLeadingSpaces(e.target.value))}
          />
        </FloatingLabel>

        {/* SUBPART */}
        <FloatingLabel controlId="subpartId" label="Subpart" className="mb-3">
          <Form.Control
            type="text"
            placeholder="Enter Subpart"
            value={subpart}
            onChange={(e) => setSubpart(trimLeadingSpaces(e.target.value))}
          />
        </FloatingLabel>

        {/* ORDER */}
        <FloatingLabel controlId="orderId" label="Order" className="mb-3">
          <Form.Control
            type="number"
            placeholder="Enter Order"
            value={order}
            onChange={(e) => handleDigitOnlyInput(e.target.value, setOrder)}
          />
        </FloatingLabel>

        {/* TOPIC */}
        <Form.Group className="mb-3">
          <DropdownCommon
            selectedValue={selectedTopic}
            setSelectedValue={(topic) => handleTopicChange(topic)}
            items={topics.map((t) => ({ _id: t._id, name: t.title }))}
            label={<span className={errors.topic && "text-danger"}>Topic *</span>}
            searchable
            searchValue={topicSearch}
            setSearchValue={setTopicSearch}
          />
          {errors.topic && <div className="text-danger mt-1">{errors.topic}</div>}
        </Form.Group>

        {/* SUBTOPIC */}
        <Form.Group className="mb-3">
          <DropdownCommon
            selectedValue={selectedSubTopic}
            setSelectedValue={setSelectedSubTopic}
            items={subTopics}
            label={<span className={errors.subTopic && "text-danger"}>Sub Topic *</span>}
            searchable
            searchValue={subTopicSearch}
            setSearchValue={setSubTopicSearch}
          />
          {errors.subTopic && <div className="text-danger mt-1">{errors.subTopic}</div>}
        </Form.Group>

        {/* SECTION */}
        <Form.Group className="mb-3">
          <DropdownCommon
            selectedValue={selectedSection ? { name: selectedSection } : null}
            setSelectedValue={(s) => setSelectedSection(s.name)}
            items={sectionList}
            label={<span className={errors.section && "text-danger"}>Section *</span>}
          />
          {errors.section && <div className="text-danger mt-1">{errors.section}</div>}
        </Form.Group>

        {/* TYPE */}
        <Form.Group className="mb-3">
          <DropdownCommon
            key={selectedType}
            selectedValue={selectedType ? { name: selectedType } : null}
            setSelectedValue={(t) => setSelectedType(t.name)}
            items={typeList}
            label={<span className={errors.type && "text-danger"}>Type *</span>}
          />
          {errors.type && <div className="text-danger mt-1">{errors.type}</div>}
        </Form.Group>

        {/* MARKS */}
        {selectedType === "Question" && (
          <FloatingLabel controlId="marksId" label="Marks" className="mb-3">
            <Form.Control
              type="number"
              placeholder="Enter Marks"
              value={marks}
              onChange={(e) => handleDigitOnlyInput(e.target.value, setMarks)}
            />
          </FloatingLabel>
        )}

        {/* DEPENDENCIES */}
        <Form.Check
          className="mb-3"
          type="checkbox"
          label="Has Dependencies?"
          checked={showDependencies}
          onChange={(e) => {
            const checked = e.target.checked;
            setShowDependencies(checked);
            if (!checked) setDependencies([]); // clear dependencies
          }}
        />

        {showDependencies && (
          <>
            <Card
              style={{ maxHeight: "200px", overflowY: "auto" }}
              onScroll={handleDependencyScroll}
            >
              <Card.Body>
                {existingQuestions.map((q) => (
                  <Form.Check key={q._id} className="mb-2">
                    <Form.Check.Input
                      type="checkbox"
                      id={q._id}
                      checked={dependencies.some((d) => d.questionId === q._id)}
                      onChange={() => handleDependencyToggle(q._id, true)}
                    />
                    <Form.Check.Label htmlFor={q._id}>
                      {q.paper} - {q.question_number}
                    </Form.Check.Label>
                  </Form.Check>
                ))}
                {loadingDependencies && (
                  <div className="text-center">
                    <Spinner size="sm" />
                  </div>
                )}
              </Card.Body>
            </Card>
            {errors.dependencies && (
              <div className="text-danger mt-1">{errors.dependencies}</div>
            )}
          </>
        )}
      </Offcanvas.Body>

      <Offcanvas.Footer>
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSave} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Offcanvas.Footer>
    </Offcanvas>
  );
}

export default QuestionEditOffcanvas;
